import axios from 'axios'

import type { LoyaltyReward, PartnerParameter, RewardPayload } from '@/types/types'
import { normalizeLoyaltyRewardFromApi } from '@/components/rewards/utils/rewardHelpers'

const normalizeReward = (reward: LoyaltyReward): LoyaltyReward => {
  normalizeLoyaltyRewardFromApi(reward)
  return reward
}

export const fetchPartnerParameters = async (): Promise<PartnerParameter[]> => {
  const { data } = await axios.get('/partner-parameters', {
    params: {
      isActive: 1
    }
  })
  return data.data as PartnerParameter[]
}

export const fetchRewardTypeIdsBySlug = async (): Promise<Record<string, number>> => {
  const { data } = await axios.get('/rewards/reward-types')
  const rows = (data?.data ?? []) as Array<{ id: number; slug: string }>
  return rows.reduce<Record<string, number>>((acc, curr) => {
    if (curr?.slug && typeof curr.id === 'number') acc[curr.slug] = curr.id
    return acc
  }, {})
}

export const fetchLoyaltyProgramRewardById = async (
  merchantId: number,
  rewardId: number,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward | null> => {
  try {
    const { data } = await axios.get(`/merchants/${merchantId}/loyalty-program/rewards/${rewardId}`, {
      params: {
        handleExternalIdAsItems
      }
    })
    return normalizeReward(data.data as LoyaltyReward)
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export const createLoyaltyProgramReward = async (
  merchantId: number,
  payload: RewardPayload,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward> => {
  const { data } = await axios.post(`/merchants/${merchantId}/loyalty-program/rewards`, payload, {
    params: {
      handleExternalIdAsItems
    }
  })
  return normalizeReward(data.data as LoyaltyReward)
}

export const updateLoyaltyProgramReward = async (
  merchantId: number,
  rewardId: number,
  payload: RewardPayload,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward> => {
  const { data } = await axios.put(`/merchants/${merchantId}/loyalty-program/rewards/${rewardId}`, payload, {
    params: {
      handleExternalIdAsItems
    }
  })
  return normalizeReward(data.data as LoyaltyReward)
}

export const dissociateLoyaltyProgramReward = async (merchantId: number, rewardId: number): Promise<void> => {
  await axios.delete(`/merchants/${merchantId}/loyalty-program/rewards/${rewardId}`)
}

export const fetchMerchantRewardById = async (
  merchantId: number,
  rewardId: number,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward | null> => {
  try {
    const { data } = await axios.get(`/merchants/${merchantId}/rewards/${rewardId}`, {
      params: {
        handleExternalIdAsItems
      }
    })
    return normalizeReward(data.data as LoyaltyReward)
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export const createReward = async (
  merchantId: number,
  payload: RewardPayload,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward> => {
  const { data } = await axios.post(`/merchants/${merchantId}/rewards`, payload, {
    params: {
      handleExternalIdAsItems
    }
  })
  return normalizeReward(data.data as LoyaltyReward)
}

export const updateReward = async (
  merchantId: number,
  rewardId: number,
  payload: RewardPayload,
  handleExternalIdAsItems = false
): Promise<LoyaltyReward> => {
  const { data } = await axios.put(`/merchants/${merchantId}/rewards/${rewardId}`, payload, {
    params: {
      handleExternalIdAsItems
    }
  })
  return normalizeReward(data.data as LoyaltyReward)
}
